/**
 * GroupPilotageTable — pilotage consolidé du groupe (Lot 5).
 * Entrées / sorties / stock / CA de chaque agence + total réseau,
 * construit sur GET /api/pilotage/group/summary (source unique Lot 1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { BarChart3, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const PERIODS = [
    { key: 'month', label: 'Mois' },
    { key: 'quarter', label: 'Trimestre' },
    { key: 'year', label: 'Année' },
];

function eur(n) {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0) + ' €';
}

export default function GroupPilotageTable({ groupId }) {
    const [period, setPeriod] = useState('month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/pilotage/group/summary?period=${period}&group_id=${groupId}`);
            setData(r.data);
        } catch (e) { /* silencieux */ }
        finally { setLoading(false); }
    }, [period, groupId]);

    useEffect(() => { load(); }, [load]);

    const t = data?.totals;

    return (
        <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15"
              data-testid="group-pilotage-table">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <div className="text-sm font-medium text-white/75 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#A855F7]" />
                    Pilotage consolidé du réseau
                </div>
                <div className="flex rounded-lg border border-white/10 overflow-hidden">
                    {PERIODS.map((p) => (
                        <button key={p.key} type="button"
                                data-testid={`group-pilotage-period-${p.key}`}
                                onClick={() => setPeriod(p.key)}
                                className={`px-2.5 py-1 text-xs font-medium transition ${
                                    period === p.key ? 'bg-[#681793] text-white' : 'text-white/40'}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="text-xs text-white/40 mb-4">
                Mouvements de mandats et chiffre d'affaires par agence — mêmes sources que les tableaux dirigeants.
            </div>

            {loading || !data ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-[#A855F7]" />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[680px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-white/40" style={MONO}>
                                <th className="py-2 pr-3">Agence</th>
                                <th className="py-2 px-3 text-right">Entrées</th>
                                <th className="py-2 px-3 text-right">Ventes</th>
                                <th className="py-2 px-3 text-right">Sorties nég.</th>
                                <th className="py-2 px-3 text-right">Stock fin</th>
                                <th className="py-2 px-3 text-right">CA réalisé HT</th>
                                <th className="py-2 pl-3 text-right">CA sécurisé HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.agencies.map((a) => (
                                <tr key={a.agency_id} className="border-t border-white/5"
                                    data-testid={`group-pilotage-row-${a.agency_id}`}>
                                    <td className="py-2.5 pr-3">
                                        <span className="text-white/90">{a.agency_name}</span>
                                        {a.city && <span className="text-white/35 text-xs"> · {a.city}</span>}
                                    </td>
                                    <td className="py-2.5 px-3 text-right text-emerald-400 tabular-nums">+{a.entries}</td>
                                    <td className="py-2.5 px-3 text-right text-emerald-400 tabular-nums">{a.positive_exits}</td>
                                    <td className={`py-2.5 px-3 text-right tabular-nums ${a.negative_exits ? 'text-red-400' : 'text-white/60'}`}>
                                        {a.negative_exits}
                                    </td>
                                    <td className="py-2.5 px-3 text-right text-white/80 tabular-nums">{a.stock_end}</td>
                                    <td className="py-2.5 px-3 text-right font-semibold text-[#C084FC] tabular-nums">{eur(a.realized_ht)}</td>
                                    <td className="py-2.5 pl-3 text-right text-white/70 tabular-nums">{eur(a.secured_ht)}</td>
                                </tr>
                            ))}
                        </tbody>
                        {t && (
                            <tfoot>
                                <tr className="border-t border-white/15 font-semibold"
                                    data-testid="group-pilotage-totals">
                                    <td className="py-2.5 pr-3 text-white">Total réseau</td>
                                    <td className="py-2.5 px-3 text-right text-emerald-400 tabular-nums">+{t.entries}</td>
                                    <td className="py-2.5 px-3 text-right text-emerald-400 tabular-nums">{t.positive_exits}</td>
                                    <td className={`py-2.5 px-3 text-right tabular-nums ${t.negative_exits ? 'text-red-400' : 'text-white/60'}`}>
                                        {t.negative_exits}
                                    </td>
                                    <td className="py-2.5 px-3 text-right text-white tabular-nums">{t.stock_end}</td>
                                    <td className="py-2.5 px-3 text-right text-[#C084FC] tabular-nums">{eur(t.realized_ht)}</td>
                                    <td className="py-2.5 pl-3 text-right text-white/80 tabular-nums">{eur(t.secured_ht)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
        </Card>
    );
}
